<template>
  <div class="store-card" v-if="uiShowBlockAtAll">
    <div class="system-info" :class="uiCardBackground">
      <div class="system-name">
        <BaseHeader
          :color="mode == 'inside' || mode == 'pro' ? 'v1-grey-3' : 'v1-grey-10'"
        >
          <template #title>
            <div class="gap-column-xs qrw-heading-xl bold">
              {{ uiSystemTitle }}
              <sup class="beta qrw-heading-xs" v-if="uiShowBeta"
                >BETA</sup
              >
            </div>
          </template>
          <template #actions>
            <BaseToggleSwitcher
              v-model="modeToggle"
              :fillBg="mode == 'inside' || mode == 'pro' ? 'v1-grey-3' : 'v1-grey-10'"
              :disabledBg="
                mode == 'inside' || mode == 'pro' ? 'v1-grey-3' : 'v1-grey-10'
              "
              :fillCircle="
                mode == 'inside' || mode == 'pro' ? 'v1-grey-3' : 'v1-grey-10'
              "
              :enabledCircle="
                mode == 'inside' || mode == 'pro' ? 'v1-grey-10' : 'white'
              "
              v-if="uiShowToggle"
            />
          </template>
        </BaseHeader>
      </div>
      <div class="todo-list pt-24" v-if="uiShowTodolist">
        <div
          class="todo-item"
          v-for="(v, k) in readyObject"
          :key="k"
          v-show="!['subtotal', 'total'].includes(k)"
        >
          <div
            class="todo-icon"
            :class="{
              active: v,
            }"
          >
            <BaseIcon icon="check" />
          </div>
          <div class="qrw-paragraph-md">
            {{ $t(`mode_settings_hints.${k}`) }}
          </div>
        </div>
      </div>
      <div class="info-list" v-else>
        <div class="info-list-scroll gap-row-xs">
          <p
            v-for="feature in uiFeatures"
            :key="feature"
            v-html="$t('new_systems_features.' + feature)"
          ></p>
        </div>
      </div>
      <div class="system-price" v-if="isNotFree">
        <h3 class="qrw-heading-xl">
          {{ subscription.price }}$<span class="qrw-heading-xs"
            >/{{ $t("store_card.month") }}</span
          >
        </h3>
      </div>
      <div class="system-price" v-else>
        <h3>
          {{ $t("new_store_card.free") }}
        </h3>
      </div>
    </div>

    <!-- ---------------------------------------- -->

    <div class="system-status" :class="{
      'disabled': uiSettingsDisabled
    }" v-if="uiAlwaysActive">
      <div class="status-container">
        <div class="status-block">
          <div class="qrw-label-sm v1-grey-6-color">
            {{ $t("new_store_card.system_status") }}
          </div>
          <span
            class="qrw-heading-xs bold"
            :class="uiSystemStatusClass"
          >
            {{ $t("new_store_card.status_" + uiSystemStatus) }}
          </span>
        </div>
      </div>
    </div>

    <div class="system-status" :class="{
      'disabled': uiSettingsDisabled
    }" v-else>
      <div class="status-container">
        <div class="status-block">
          <div class="qrw-label-sm v1-grey-6-color">
            {{ $t("new_store_card.subscription_status") }}
          </div>
          <span
            class="qrw-heading-xs bold orange-4-color"
            v-if="isPendingBankApproval"
            >
              {{ $t('new_store_card.status_pending_approval') }} | {{ pending_approval_time}}
            </span
          >
          <span
            class="qrw-heading-xs bold"
            :class="uiSubscriptionStatus"
            v-else
            >{{
              isSubscriptionActive
                ? $t("new_store_card.status_subscribed")
                : $t("new_store_card.status_not_subscribed")
            }}</span
          >
        </div>

        <div class="status-block">
          <div
            class="qrw-label-sm v1-grey-6-color"
          >
            {{ $t("new_store_card.system_status") }}
          </div>
          <span
            class="qrw-heading-xs bold"
            :class="uiSystemStatusClass"
          >
            {{ $t("new_store_card.status_" + uiSystemStatus) }}
          </span>
        </div>

        <template v-if="isSubscriptionActive">
          <div class="status-block" v-if="!isPendingBankApproval">
            <div class="qrw-label-sm v1-grey-6-color">
              {{ $t("new_store_card.next_payment") }}
            </div>
            <div class="qrw-heading-xs bold" :class="{
              'red-4-color': isSubscriptionExpired
            }">
              {{ nextPaymentDate | formatDate }}
            </div>

            <div
              v-if="!isSubscriptionExpired"
              class="warning-box qrw-paragraph-xs"
            >
              <BaseIcon icon="alert-circle-regular" style="font-size: 18px;" :size="null" />
              <small v-html="$t('new_store_card.recurring_enabled')"> </small>
            </div>
            <div v-else class="warning-box red-4-color qrw-paragraph-xs">
              <BaseIcon icon="allert-regular" style="font-size: 18px;" :size="null" />
              <small v-html="$t('new_store_card.recurring_failed', {
                days: autoCancelDaysAmount
              })"></small>
            </div>
          </div>

          <div class="status-block" v-if="subscription.attached_card_mask">
            <div class="qrw-label-sm v1-grey-6-color">
              {{ $t("new_store_card.connected_card") }}
            </div>
            <div class="qrw-heading-xs v1-grey-10-color bold" :class="{
              'red-4-color': isSubscriptionExpired && !isPendingBankApproval
            }">
              {{ subscription.attached_card_mask }}
            </div>
          </div>
        </template>

        <div class="status-block" v-else-if="hasFreemiumStatusBar">
          <div
            class="qrw-label-sm"
            :class="[
              freemiumOrdersUsed >= maxFreemiumOrders
                ? 'red-4-color'
                : 'orange-4-color',
            ]"
          >
            {{
              isLegacyFreemium
                ? $t("new_store_card.freemium_count")
                : $t("new_store_card.trial_period")
            }}
          </div>
          <span class="free-order-counter">
            <span
              class="free-order-primary qrw-heading-md bold"
              :class="{
                limit: freemiumOrdersUsed >= maxFreemiumOrders,
              }"
            >
              {{ freemiumOrdersUsed }} </span
            ><span class="free-order-secondary qrw-label-sm" :class="{
                limit: freemiumOrdersUsed >= maxFreemiumOrders,
              }"
              >/{{ maxFreemiumOrders }}

              <template v-if="!isLegacyFreemium">{{
                $t("units.days")
              }}</template>
            </span>
          </span>

          <div class="status-bar">
            <div
              :class="{
                daybar: true,
                'active-day': index < freemiumOrdersUsed,
                'limit-bg': freemiumOrdersUsed >= maxFreemiumOrders,
              }"
              v-for="(item, index) in maxFreemiumOrders"
              :key="index"
            ></div>
          </div>
        </div>

        <template v-else-if="hasNextPaymentDate">
          <div class="status-block">
            <div class="qrw-label-sm v1-grey-6-color">
              {{ $t(isSubscriptionExpired ? "new_store_card.ended_at" : "new_store_card.paid_till") }}
            </div>
            <div class="qrw-heading-xs bold" :class="{
              'red-4-color': isSubscriptionExpired
            }">
              {{ subscription.paid_until | formatDate }}
            </div>
          </div>
        </template>


      </div>

      <!-- ------------------------------------------------ -->

      <div class="action-bar" v-if="!isSubscriptionActive">
        <small
          class="d-block"
          v-if="freemiumOrdersUsed < maxFreemiumOrders && hasFreemiumStatusBar"
        >
          {{ $t("new_store_card.freemium_note", { mode: uiSystemTitle }) }}
        </small>
        <small class="d-block v1-grey-7-color" v-else-if="!isSubscriptionActive && !isSubscriptionExpired">
          {{ $t('new_store_card.paid_text', { mode: uiSystemTitle })}}
        </small>
        <small class="d-block" v-else>
          {{ $t("new_store_card.freemium_limit", { mode: uiSystemTitle }) }}
        </small>
        <BaseButton
          @click="
            is_buying = true;
            is_changing_card = false;
          "
          class="subscribe-btn mt-16"
          bold
          size="lg"
          variant="primary"
          v-if="canDoActions"
          >{{ $t("new_store_card.subscribe_btn") }}
        </BaseButton>
      </div>

      <div class="action-bar btns" v-else-if="canDoActions">
        <BaseButton
          class="subscribe-btn mr20"
          @click="onChangeCard"
          size="lg"
          variant="secondary"
          v-if="!isSubscriptionExpired"
        >
          {{ $t("new_store_card.change_card_btn") }}
        </BaseButton>
        <BaseButton
          class="subscribe-btn mr20"
          @click="onChangeCard"
          size="lg"
          variant="primary"
          v-else
        >
          {{ $t("new_store_card.update_payment") }}
        </BaseButton>
        <BaseButton
          class="subscribe-btn"
          size="lg"
          color="red-4"
          variant="secondary"
          @click="is_cancelling = true"
        >
          {{ $t("new_store_card.cancel_btn") }}
        </BaseButton>
      </div>
    </div>

    <DangerConfirmModal
      @confirm="cancelSubscription"
      @cancel="is_cancelling = null"
      v-if="is_cancelling"
      >{{ $t("subscription.cancel") }}</DangerConfirmModal
    >

    <DangerConfirmModal
      @confirm="cancelSubscription"
      @cancel="cancel_reconfirm_modal = false"
      :cancelButton="$t('new_store_card.reconfirm_btn_cancel')"
      :confirmButton="$t('new_store_card.reconfirm_btn_confirm')"
      v-if="cancel_reconfirm_modal"
    >
      {{
        $t("new_store_card.reconfirm_cancel", {
          mode: uiSystemTitle,
        })
      }}
    </DangerConfirmModal>

    <PopUp
      @close="is_buying = null"
      v-if="is_buying"
      v-show="!is_changing_card"
      :title="$t('subscribe_period.title')"
    >
      <div>
        <div class="subscription-pop-up-container">
          <SubscriptionPriceCard
            @select="selected_periodicity = 'month'"
            :active="selected_periodicity == 'month'"
            :length="1"
            @subscribe="proceedToPayment"
            :profit="0"
            :month_price="subscription.price"
          />
          <SubscriptionPriceCard
            @select="selected_periodicity = 'year'"
            :month_price="Math.floor(subscription.price * 0.8)"
            :active="selected_periodicity == 'year'"
            @subscribe="proceedToPayment"
            :profit="Math.floor(subscription.price * 12 * 0.2)"
            :length="12"
          />
        </div>
      </div>

      <form
        method="POST"
        action="https://www.liqpay.ua/api/3/checkout"
        accept-charset="utf-8"
        ref="subscribeForm"
        v-if="subscribe_form"
      >
        <input type="hidden" name="data" :value="subscribe_form.data" />
        <input
          type="hidden"
          name="signature"
          :value="subscribe_form.signature"
        />
      </form>
    </PopUp>
  </div>
</template>

<style scoped>
.store-card {
  display: flex;
  width: 100%;
  min-height: 400px;
  margin-bottom: 24px;
  box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  background: #fafafa;
}

.system-info {
  border-radius: 8px 0 0 8px;
  min-width: 400px;
  max-width: 400px;
  display: flex;
  flex-flow: column;
}

.system-name h1 {
  font-weight: bold;
}

.system-name {
  padding: 24px 24px 0 24px;
}

.info-list {
  overflow: auto;
  max-height: 260px;

  padding: 0 24px;
  height: 100%;
}

.system-price {
  /* margin-top: 24px; */
  position: relative;
  bottom: 0;
  width: 100%;

  box-sizing: border-box;

  padding: 16px 24px;
}

.system-price h3 {
  font-weight: bold;
}

h3 span {
  font-weight: 400;
  font-size: 20px;
}

.system-status {
  width: 100%;
  padding: 24px;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  position: relative;
}

.status-bar {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.daybar {
  background-color: var(--v1-grey-5-color);
  height: 2px;
  width: -webkit-fill-available;
  margin-right: 4px;
}

.daybar:last-child {
  margin-right: 0;
}

.active-day {
  background-color: var(--orange-4-color);
}

.warning-box {
  display: flex;
  column-gap: 4px;
  align-items: center;
}

.beta {
  color: var(--red-4-color);
}

.subscribe-btn {
  width: 100%;
  justify-content: center;
  text-transform: uppercase;
  font-weight: bold;
}

.status-container {
  display: flex;
  flex-flow: column wrap;

  row-gap: 24px;
}

.action-bar {
  position: relative;
  bottom: 0;
}

.action-bar.btns {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  column-gap: 8px;
}

.action-bar small {
  padding: 16px 0;
  font-size: 16px;
}

.active {
  color: var(--green-5-color);
}

.inactive {
  color: var(--red-4-color);
}

.todo-item {
  display: flex;
  align-items: center;
  align-content: center;
  column-gap: 8px;
}

.todo-icon {
  border-radius: 50%;
  padding: 4px;
  width: 24px;
  height: 24px;

  text-align: center;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;

  box-sizing: border-box;

  background: var(--v1-grey-10-color);
  color: var(--v1-grey-3-color);

  border: 1px solid var(--v1-grey-3-color);
}

.todo-icon.active {
  color: var(--v1-grey-10-color);
  background: var(--v1-grey-3-color);
  border: 1px solid transparent;
}

.todo-list {
  display: flex;
  row-gap: 16px;
  flex-direction: column;
  max-height: 260px;
  overflow: auto;
}

.subscription-pop-up-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  margin-top: 5.555555555555555vh;
}

.info-list-scroll {
  padding: 24px 0;
}

.status-block {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
}

.black-store-card {
  background: var(--v1-grey-10-color);
  color: var(--v1-grey-1-color);
}

.free-order-primary {
  color: var(--orange-4-color);
}

.free-order-counter {
  display: flex;
  align-items: center;
}

.free-order-secondary {
  color: var(--orange-1-color);
  align-self: flex-end;
}

.limit {
  color: var(--red-4-color);
}

.limit-bg {
  background-color: var(--red-4-color);
}

small {
  font-size: 12px;
}

/* .system-status.disabled {
  opacity: 0.52;
  cursor: not-allowed;
} */
</style>



<script>
import SubscriptionAPI from "@/api/subscription";
import PopUp from "@/components/PopUp.vue";
import SubscriptionPriceCard from "@/components/subscriptions/SubscriptionPriceCard.vue";

import CafeAPI from "@/api/cafe";

import moment from "moment";

export default {
  props: {
    mode: String,
  },
  data() {
    return {
      is_cancelling: false,
      is_buying: false,
      is_changing_card: false,

      selected_periodicity: "month",

      cancel_reconfirm_modal: false,

      cancel_reconfirm_modal: false,

      approval_timer: null,
      pending_approval_time: '15:00',

      subscribe_form: {},
    };
  },
  computed: {
    cafe() {
      return this.$store.state.cafe;
    },
    subscription() {
      if (!this.uiCanSetup) return null;
      return this.$store.getters["subscriptions/all"][this.mode];
    },
    nextPaymentDate() {
      if (this.mode == "pro")
        return moment(this.subscription.paid_until).toDate();
      if (!this.isSubscriptionActive) return new Date();
      if (!this.subscription.paid_until) return new Date();

      const is_expired = moment(this.subscription.paid_until).isBefore(
        moment()
      );

      return is_expired
        ? moment().add(1, "day").toDate()
        : moment(this.subscription.paid_until).toDate();
    },
    isPendingBankApproval() {
      return !!this.subscription && this.subscription.liqpay_id && (!this.subscription.last_paid_at || moment(this.subscription.last_paid_at).isBefore(this.subscription.card_attached_at));
    },
    maxFreemiumOrders() {
      return this.isLegacyFreemium
        ? 30
        : this.subscription.trial_period_total_duration || 14;
    },
    freemiumOrdersUsed() {
      if (!this.subscription) return 0;

      let val = 0;

      if (this.isLegacyFreemium) {
        val = this.maxFreemiumOrders - this.subscription.freemium_orders_left;
      } else {
        val =
          this.maxFreemiumOrders -
          moment(this.subscription.trial_period_until).diff(moment(), "days");
      }

      return val > this.maxFreemiumOrders ? this.maxFreemiumOrders : val;
    },
    isPaid() {
      if (this.isSubscriptionActive) {
        return true;
      } else {
        if (!this.subscription) return false;

        if (!this.isSubscriptionExpired) return true;

        if (this.subscription.using_freemium) {
          return this.freemiumOrdersUsed < this.maxFreemiumOrders;
        } else {
          if (
            this.subscription.trial_period_until &&
            moment(this.subscription.trial_period_until).isAfter(moment())
          ) {
            return true;
          }

          return this.freemiumOrdersUsed < this.maxFreemiumOrders;
        }
      }
    },
    isSubscriptionExpired() {
      if (!this.subscription) return false;
      if (!this.subscription.paid_until) return true;

      return moment().isAfter(this.subscription.paid_until);
    },
    uiSystemStatus() {
      if (this.mode == 'reserve') return this.modeToggle ? 'active' : 'inactive';

      return this.modeToggle && this.isPaid  && !this.isSubscriptionExpired ? "active" : "inactive";
    },
    uiSystemStatusClass() {
      if (!this.isPaid && !this.uiAlwaysActive) return "inactive";

      return this.modeToggle  && !this.isSubscriptionExpired  ? "active" : 'inactive';
    },
    isSubscriptionActive() {
      return this.subscription && this.subscription.liqpay_id;
    },
    hasFreemiumStatusBar() {
      if (this.mode == "hotels") return false;

      const trp = this.subscription.trial_period_until;

      if (!trp) return false;

      if (this.subscription.paid_until && moment().isBefore(this.subscription.paid_until)) {
        return false;
      }

      return moment().isBefore(trp);

      // return this.mode !== "hotels" && (this.subscription.using_freemium || this.subscription.trial_period_until);
    },
    uiAlwaysActive() {
      return this.mode == "reserve";
    },
    uiShowToggle() {
      return true;
    },
    uiSettingsDisabled() {
      if (this.isSubscriptionActive) return false;

      return !this.modeToggle;
    },
    hasNextPaymentDate() {
      if (!this.subscription) return false;
      return !!this.subscription.paid_until;
    },
    uiSubscriptionStatus() {
      if (!this.subscription) return "inactive";
      if (this.isPendingBankApproval) return 'pending_approval';
      return this.isSubscriptionActive ? "active" : "inactive";
    },
    uiFeatures() {
      return (
        {
          preview: ["custom_page", "menu_translation", "pos_integration"],
          inside: [
            "inside_orders",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          delivery: [
            "delivery_orders",
            "sms_verification",
            "push_notifications",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          preorder: [
            "takeaway_orders",
            "sms_verification",
            "push_notifications",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          reserve: [
            "reserve_orders",
            "sms_verification",
            "push_notifications",
            "reserve_deposit",
            "reserve_calendar",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          pro: [
            "custom_page",
            "pro_inside_orders",
            "pro_takeaway_orders",
            "pro_delivery_orders",
            "pro_reserve_orders",
          ],
        }[this.mode] || []
      );
    },
    uiCardBackground() {
      return {
        delivery: "blue-2-bg",
        preorder: "yellow-3-bg",
        inside: "black-store-card",
        reserve: "green-1-bg",
        pro: "black-store-card",
      }[this.mode];
    },
    uiSystemTitle() {
      return {
        inside: "INSIDE",
        preorder: "TAKEAWAY",
        delivery: "DELIVERY",
        reserve: "RESERVE",
        preview: "PREVIEW",
        hotels: "ROOMS",
        pro: "PRO",
      }[this.mode];
    },
    uiCanSetup() {
      return this.mode != "reserve";
    },
    uiShowBeta() {
      return this.mode == "reserve" || this.mode == "hotels";
    },
    canDoActions() {
      return this.checkUserPemission("*");
    },
    uiShowBlockAtAll() {
      if (this.mode == "pro") {
        return true;
      } else {
        return true;
      }
    },
    proRealMode() {
      if (this.mode !== "pro") return this.mode;

      return this.$route.name;
    },
    modeToggle: {
      get() {
        if (!this.cafe || !this.cafe.modes) return false;

        return this.cafe.modes[this.proRealMode];
      },
      set(value) {
        this.$store.commit("toggleCafeMode", {
          mode: this.proRealMode,
          value,
        });
        this.$store.dispatch("saveCafe");

        this.$store.dispatch("auditlog/write", {
          type: value ? "mode_enable" : "mode_disable",
          payload: {
            mode: this.proRealMode,
          },
        });

        CafeAPI.setMode(this.cafe._id, this.proRealMode, value).then((data) => {
          if (data.changed) {
            this.$store.dispatch("subscriptions/fetchAll", this.cafe._id);
          }
        });

        if (!value && this.isSubscriptionActive) {
          this.cancel_reconfirm_modal = true;
        }
      },
    },
    isNotFree() {
      return this.subscription && this.subscription.mode != "reserve";
    },
    uiShowTodolist() {
      return false;
      // return this.uiSystemStatus == 'active' && this.readyObject;
    },
    readyObject() {
      const key =
        this.proRealMode.substring(0, 1).toUpperCase() +
        this.proRealMode.substring(1);
      return this.$store.getters[`ready${key}`];
    },
    isLegacyFreemium() {
      return !!this.subscription && this.subscription.using_freemium;
    },
    autoCancelDaysAmount() {
      if (!this.isSubscriptionActive) return null;

      return Math.max(Math.round(moment(this.subscription.paid_until).add(4, 'days').diff(
        moment(),
        'days',
        true
      )), 1);
    }
  },
  methods: {
    checkUserPemission(perm) {
      return this.$store.getters["checkUserPemission"](perm);
    },
    onChangeCard() {
      this.is_changing_card = true;
      this.selected_periodicity = this.subscription.periodicity;
      this.is_buying = true;

      this.proceedToPayment();
    },
    proceedToPayment() {
      // window.location.href = `https://qrwaiter.com.ua/api/subscription/form/${
      //   this.subscription._id
      // }/html`;
      SubscriptionAPI.generateForm(
        this.subscription._id,
        this.selected_periodicity,
        this.is_changing_card
      )
        .then((data) => {
          this.subscribe_form = data.form;

          this.$nextTick(() => {
            setTimeout(() => {
              this.$refs.subscribeForm.submit();
            }, 500);
          });
        })
        .catch((error) => {
          this.$store.dispatch("showError", "subscription_error");
        });
    },
    cancelSubscription() {
      this.is_cancelling = false;
      this.cancel_reconfirm_modal = false;

      SubscriptionAPI.cancelSubscription(this.subscription._id)
        .then((data) => {
          this.$store.commit("subscriptions/setSingleSubscription", {
            mode: this.subscription.mode,
            subscription: data.subscription,
          });
        })
        .catch((error) => {
          this.$store.dispatch("showError", "subscription_error");
          this.is_cancelling = false;
        });
    },
    tickApprovalTimer() {
      if (this.isPendingBankApproval) {
        const df = moment(this.subscription.card_attached_at).add(15, 'minutes').diff(moment());
        if (df <= 0) return '00:00';
        this.pending_approval_time = moment.utc(df).format('mm:ss');
      }
    }
  },
  mounted() {
    this.approval_timer = setInterval(this.tickApprovalTimer, 1000);
    this.tickApprovalTimer();
  },
  beforeDestroy() {
    clearInterval(this.tickApprovalTimer);
  },
  components: {
    SubscriptionPriceCard,
    PopUp,
  },
};
</script>
